import { Injectable } from '@angular/core';
import { ModuleAccessLevel } from '../../constants-types/Enums';
import {
    ModuleAccountType,
    ModuleExperienceType,
    ModuleName,
    SoftwareModuleAccessConfiguration,
    TimeTrial
} from '../../core-data-model/ModuleAccess/ModuleAccess.model';
import { Calculated } from '../../core-utilities/Calculated.decorator';
import { ModuleAccessApiConnect } from './ModuleAccess.connect';

@Injectable({ providedIn: 'root' })
export class ModuleAccessService {
    public moduleAccessConfig: SoftwareModuleAccessConfiguration;

    constructor(private api: ModuleAccessApiConnect) {}

    get configString() {
        return JSON.stringify(this.moduleAccessConfig);
    }

    get hasHire(): boolean {
        return this.hasAccessToProduct(ModuleName.Hire);
    }

    get hasHireHero(): boolean {
        return this.hasAccessToProduct(ModuleName.HireHero);
    }

    get hasInspire(): boolean {
        return this.hasInspire1 || this.hasInspire2;
    }

    get hasInspire1(): boolean {
        return this.hasAccessToProduct(ModuleName.Inspire);
    }

    get hasInspire2(): boolean {
        return this.hasAccessToProduct(ModuleName.Inspire2);
    }

    get hasDesign(): boolean {
        return this.hasDesign1 || this.hasDesign2;
    }

    get isDesignStandalone(): boolean {
        return (
            this.hasDesign &&
            !(this.hasHire || this.hasInspire || this.hasDiagnose)
        );
    }

    get isDiagnoseStandalone(): boolean {
        return (
            this.hasDiagnose &&
            !(this.hasHire || this.hasInspire || this.hasDesign)
        );
    }

    get isInspireStandalone() {
        return (
            this.hasInspire &&
            !(this.hasDesign || this.hasDiagnose || this.hasHire)
        );
    }

    get isTeamDiscoverySelfServe(): boolean {
        return this.isProductSelfServe(ModuleName.Design);
    }

    get isTeamDiscoveryFree(): boolean {
        return (
            this.moduleAccessConfig.softwareModuleAccessConfig[
                this.getConfigIndex(ModuleName.Design)
            ]?.moduleAccountType === ModuleAccountType.Free
        );
    }

    get isLegacyWithDesign2(): boolean {
        return this.hasDesign2;
    }

    get hasDesign1(): boolean {
        return this.hasAccessToProduct(ModuleName.Design);
    }

    get hasDesign2(): boolean {
        return this.hasAccessToProduct(ModuleName.Design2);
    }

    /**
     * Returns true if the current user / company has Diagnose 1 or Diagnose 2.
     */
    get hasDiagnose(): boolean {
        return this.hasDiagnose1 || this.hasDiagnose2;
    }

    get hasDiagnose1(): boolean {
        return this.hasAccessToProduct(ModuleName.Diagnose);
    }

    get hasDiagnose2(): boolean {
        return this.hasAccessToProduct(ModuleName.Diagnose2);
    }

    get isDiagnoseSelfServe(): boolean {
        return this.isProductSelfServe(ModuleName.Diagnose2);
    }

    get isHireHeroFree(): boolean {
        return (
            this.moduleAccessConfig.softwareModuleAccessConfig[
                this.getConfigIndex(ModuleName.HireHero)
            ]?.moduleAccountType === ModuleAccountType.Free
        );
    }

    get isHireHeroStandalone(): boolean {
        return (
            this.hasHireHero &&
            !(
                this.hasHire ||
                this.hasInspire ||
                this.hasDiagnose ||
                this.hasDesign
            )
        );
    }

    get isHireHeroSelfServe(): boolean {
        return this.isProductSelfServe(ModuleName.HireHero);
    }

    /**
     * Returns true if the company has access to the Perform module in the SMAC.
     */
    get hasPerform(): boolean {
        return this.hasAccessToProduct(ModuleName.Perform);
    }

    /**
     * Returns true if the company has access to the Perform module in the SMAC.
     */
    get hasOnlyPerform(): boolean {
        return this.hasOnlyOneProduct(ModuleName.Perform);
    }

    get hasAnySelfServeModule(): boolean {
        return (
            (this.hasHireHero && this.isHireHeroSelfServe) ||
            (this.hasDiagnose && this.isDiagnoseSelfServe) ||
            (this.hasDesign && this.isTeamDiscoverySelfServe)
        );
    }

    /**
     * Only returns true if the company has any of the 2.0 modules.
     * It is invalid to have a PI2 module and a legacy module at the same time.
     *
     * Perform is not included because Perform can be available for both 1.0 and 2.0.
     */
    get hasV2ModuleAccess(): boolean {
        return (
            this.hasHireHero ||
            this.hasDesign2 ||
            this.hasDiagnose2 ||
            this.hasInspire2
        );
    }

    get accountClassificationType(): string {
        return this.moduleAccessConfig.accountClassification.classificationType;
    }

    @Calculated.fromImmutable(
        (me: ModuleAccessService) =>
            me.moduleAccessConfig.softwareModuleAccessConfig
    )
    getValueMetricForProduct(metricName: string, module: ModuleName) {
        const valueMetricLimit =
            this.moduleAccessConfig.softwareModuleAccessConfig
                .find((m) => m.name === module)
                ?.valueMetrics?.find((vm) => vm.name === metricName)?.limit;
        return valueMetricLimit !== null ? valueMetricLimit : -1;
    }

    async fetchConfig() {
        this.moduleAccessConfig =
            await this.api.getSoftwareModuleAccessConfiguration();
    }

    isProductSelfServe(module: ModuleName): boolean {
        return (
            this.hasAccessToProduct(module) &&
            this.moduleAccessConfig.softwareModuleAccessConfig[
                this.getConfigIndex(module)
            ]?.moduleExperienceType === ModuleExperienceType.SelfServe
        );
    }

    /**
     * Is the provided module in a timetrial state?
     *
     * @param module
     * @returns bool
     */
    isTimeTrialEnabled(module: ModuleName) {
        return !!this.moduleAccessConfig.softwareModuleAccessConfig[
            this.getConfigIndex(module)
        ]?.timeTrials[0];
    }

    /**
     * Get TimeTrial info for the requested module
     *
     * @param module
     * @returns TimeTrial
     */
    getTimeTrialDetails(module: ModuleName): TimeTrial {
        return this.moduleAccessConfig.softwareModuleAccessConfig[
            this.getConfigIndex(module)
        ]?.timeTrials[0];
    }

    /**
     * Is the provided module in a paid state?
     *
     * @see isPaidAccountAnyModule for all modules. This should likely be renamed to be module specific.
     * @param module
     * @returns bool
     */
    isPaidAccount(module: ModuleName): boolean {
        return (
            this.hasAccessToProduct(module) &&
            this.moduleAccessConfig.softwareModuleAccessConfig[
                this.getConfigIndex(module)
            ]?.moduleAccountType === ModuleAccountType.Paid
        );
    }

    /**
     * Determine if a module is in a time trial expired state
     *
     * @param module
     * @returns bool
     */
    isTimeTrialExpired(module: ModuleName): boolean {
        const currentDate = new Date(Date.now());
        return (
            !this.isPaidAccount(module) &&
            this.hasAccessToProduct(module) &&
            this.moduleAccessConfig.softwareModuleAccessConfig[
                this.getConfigIndex(module)
            ]?.timeTrials.every(
                (t) => currentDate.getTime() > new Date(t.endDate).getTime()
            )
        );
    }

    /**
     * Is any module in the account paid?
     *
     * @returns bool
     */
    anyModuleIsPaid(): boolean {
        return this.moduleAccessConfig.softwareModuleAccessConfig.some(
            (e) =>
                e.moduleAccountType &&
                e.moduleAccountType === ModuleAccountType.Paid &&
                e.accessLevel > ModuleAccessLevel.NoAccess
        );
    }

    getConfigIndex(module: ModuleName): number {
        return this.moduleAccessConfig?.softwareModuleAccessConfig.findIndex(
            (m) => m.name === module
        );
    }

    private hasAccessToProduct(module: ModuleName): boolean {
        if (!module || !this.moduleAccessConfig) {
            return false;
        }

        const configIndex = this.getConfigIndex(module);
        if (configIndex === -1) {
            return false;
        }
        return (
            this.moduleAccessConfig.softwareModuleAccessConfig[configIndex]
                ?.accessLevel !== ModuleAccessLevel.Unknown &&
            this.moduleAccessConfig.softwareModuleAccessConfig[configIndex]
                ?.accessLevel !== ModuleAccessLevel.NoAccess
        );
    }

    private hasOnlyOneProduct(module: ModuleName): boolean {
        if (!module || !this.moduleAccessConfig) {
            return false;
        }

        const modulesHasAccessTo =
            this.moduleAccessConfig.softwareModuleAccessConfig.filter(
                (m) => m.accessLevel > ModuleAccessLevel.NoAccess
            );

        return (
            modulesHasAccessTo.length == 1 &&
            modulesHasAccessTo[0].name == module
        );
    }
}
